import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from './Header';
import { CONFIG, ERROR_MESSAGES, SUCCESS_MESSAGES } from '../constants/config';
import { validateSubmissionForm, validateAmount, formatCurrency, sanitizeAmount, normalizeUrl } from '../utils/validation';
import { ApiService, PaymentService } from '../services/api';
import PixelArtEditor from './PixelArtEditor';
import CurrencyService from '../utils/currencyService';

const CreateSubmission = () => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    url: '',
    amount: CONFIG.DEFAULT_INITIAL_PAYMENT,
    image_data: null,
    pixel_data: null
  });
  
  const [showPixelEditor, setShowPixelEditor] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [paymentRegion, setPaymentRegion] = useState('us');
  const [detectedRegion, setDetectedRegion] = useState(null);
  const [currencyInfo, setCurrencyInfo] = useState(null);

  // Detect user's region and get live currency rates
  useEffect(() => {
    const detectRegionAndRates = async () => {
      try {
        // Try to get user's timezone first (most reliable)
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        let region = 'us'; // default
        let detectedCountry = 'US';

        // European timezones
        if (timezone.includes('Europe/')) {
          region = 'eu';
          detectedCountry = timezone.split('/')[1]?.substring(0, 2)?.toUpperCase() || 'EU';
        }

        // Try browser language as secondary indicator
        const language = navigator.language || navigator.languages[0];
        if (language?.startsWith('de') || language?.startsWith('fr') || language?.startsWith('nl') || 
            language?.startsWith('es') || language?.startsWith('it')) {
          region = 'eu';
        }

        setDetectedRegion(detectedCountry);
        setPaymentRegion(region);

        // Get live currency info from the service
        const currencyInfo = await CurrencyService.getCurrencyInfo(region);
        setCurrencyInfo(currencyInfo);

        console.log(`Detected region: ${detectedCountry}, using ${region.toUpperCase()} payment flow`);
        console.log('Currency info:', currencyInfo);
      } catch (error) {
        console.log('Region detection or currency fetch failed, defaulting to US');
        setPaymentRegion('us');
        setCurrencyInfo({
          currency: 'USD',
          rate: 1.0,
          symbol: '$',
          isLive: false
        });
      }
    };

    detectRegionAndRates();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Handle amount field specially to ensure it's properly sanitized
    const processedValue = name === 'amount' ? sanitizeAmount(value) : value;
    
    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handlePixelArtSave = (imageData, pixelGrid) => {
    setFormData(prev => ({
      ...prev,
      image_data: imageData,
      pixel_data: pixelGrid
    }));
    setImagePreview(imageData);
    setShowPixelEditor(false);
    
    // Clear file error if it exists
    if (errors.file) {
      setErrors(prev => ({
        ...prev,
        file: ''
      }));
    }
  };

  const handlePixelEditorCancel = () => {
    setShowPixelEditor(false);
  };

  const removeImage = () => {
    setFormData(prev => ({
      ...prev,
      image_data: null,
      pixel_data: null
    }));
    setImagePreview(null);
  };

  const validateForm = () => {
    const validation = validateSubmissionForm(formData);
    
    // Add terms acceptance validation
    if (!termsAccepted) {
      validation.errors.terms = 'You must accept the Terms and Conditions to continue';
      validation.isValid = false;
    }
    
    setErrors(validation.errors);
    return validation.isValid;
  };

  const handleTermsChange = (e) => {
    setTermsAccepted(e.target.checked);
    // Clear terms error when user accepts
    if (e.target.checked && errors.terms) {
      setErrors(prev => ({
        ...prev,
        terms: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Check minimum payment amount
    if (!validateAmount(formData.amount) || formData.amount === '' || formData.amount < CONFIG.MINIMUM_PAYMENT_AMOUNT) {
      setErrors(prev => ({
        ...prev,
        amount: ERROR_MESSAGES.MINIMUM_PAYMENT_CREATE
      }));
      return;
    }

    try {
      setSubmitting(true);

      // Prepare submission data for Stripe metadata
      const submissionData = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        url: normalizeUrl(formData.url) || null,
        profile_pic_url: imagePreview // Use pixel art as profile pic
      };

      // Create Stripe checkout session for new submission
      const checkoutResponse = await ApiService.createCheckoutSession(
        null, // No existing submission ID
        parseFloat(formData.amount),
        submissionData, // Pass submission data to be created after payment
        paymentRegion // Pass detected payment region
      );

      // Redirect to Stripe Checkout
      const result = await PaymentService.processStripePayment(checkoutResponse.session_id);
      
      // If we reach here, something went wrong (Stripe should redirect)
      if (result.testing) {
        alert('Testing mode: Payment flow completed');
        window.location.href = '/';
      }
    } catch (err) {
      console.error('Payment error:', err);
      setErrors(prev => ({
        ...prev,
        submit: err.response?.data?.error || ERROR_MESSAGES.PAYMENT_FAILED
      }));
    } finally {
      setSubmitting(false);
    }
  };

  if (showPixelEditor) {
    return (
      <div>
        <Header />
        <div className="container">
          <div className="main-content">
            <PixelArtEditor
              onSave={handlePixelArtSave}
              onCancel={handlePixelEditorCancel}
              initialData={formData.pixel_data}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Header />
      
      <div className="container">
        <div className="main-content">
          <div className="form-container">
            <h2 style={{ textAlign: 'center', marginBottom: '30px', color: '#00ffff' }}>
              Create Your Banner
            </h2>
        
            <div style={{ 
              background: 'rgba(0, 255, 255, 0.1)', 
              border: '2px solid #00ffff', 
              borderRadius: '8px', 
              padding: '20px', 
              marginBottom: '30px',
              textAlign: 'center'
            }}>
              <h3 style={{ color: '#00ffff', marginBottom: '15px' }}>Rules & Requirements</h3>
              <ul style={{ textAlign: 'left', color: '#cccccc', lineHeight: '1.6' }}>
                <li>Minimum initial payment: <strong>{formatCurrency(CONFIG.MINIMUM_PAYMENT_AMOUNT)}</strong></li>
                <li>Your banner will appear on the leaderboard after payment</li>
                <li>Rankings are determined by total payments received</li>
                <li>Be respectful and follow community guidelines</li>
                <li>Create unique pixel art instead of uploading files</li>
              </ul>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="title">Title *</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="Enter an engaging title for your submission"
                  maxLength={CONFIG.MAX_TITLE_LENGTH}
                />
                {errors.title && <div className="error" style={{ marginTop: '5px' }}>{errors.title}</div>}
              </div>

              <div className="form-group">
                <label htmlFor="description">Description *</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  className="form-textarea"
                  placeholder="Tell people about your project, idea, or cause. Be detailed and engaging!"
                  rows="6"
                />
                {errors.description && <div className="error" style={{ marginTop: '5px' }}>{errors.description}</div>}
              </div>

              <div className="form-group">
                <label htmlFor="url">Website/Link (Optional)</label>
                <input
                  type="text"
                  id="url"
                  name="url"
                  value={formData.url}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="https://your-website.com"
                />
                {errors.url && <div className="error" style={{ marginTop: '5px' }}>{errors.url}</div>}
              </div>

              <div className="form-group">
                <label htmlFor="profile-pic">Profile Picture (Pixel Art)</label>
                <div className="pixel-art-section">
                  {imagePreview ? (
                    <div style={{ textAlign: 'center', marginTop: '10px' }}>
                      <img 
                        src={imagePreview}
                        alt="Pixel art preview"
                        style={{
                          width: '150px',
                          height: '150px',
                          border: '2px solid #00ffff',
                          imageRendering: 'pixelated'
                        }}
                      />
                      <div style={{ marginTop: '10px' }}>
                        <button 
                          type="button" 
                          onClick={() => setShowPixelEditor(true)}
                          style={{
                            background: '#ffd700',
                            color: 'black',
                            border: 'none',
                            padding: '8px 15px',
                            marginRight: '10px',
                            cursor: 'pointer'
                          }}
                        >
                          Edit Pixel Art
                        </button>
                        <button 
                          type="button" 
                          onClick={removeImage}
                          style={{
                            background: '#666',
                            color: 'white',
                            border: 'none',
                            padding: '8px 15px',
                            cursor: 'pointer'
                          }}
                        >
                          Remove
                        </button>
                      </div>
                      <div style={{ color: '#00ff00', fontSize: '0.9rem', marginTop: '5px' }}>
                        Pixel art created successfully!
                      </div>
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '20px', border: '2px dashed #666', marginTop: '10px' }}>
                      <div style={{ fontSize: '48px', marginBottom: '10px' }}>🎨</div>
                      <p style={{ color: '#cccccc', marginBottom: '15px' }}>
                        Create your 50x50 pixel art masterpiece!
                      </p>
                      <button
                        type="button"
                        onClick={() => setShowPixelEditor(true)}
                        style={{
                          background: '#00ffff',
                          color: 'black',
                          border: 'none',
                          padding: '10px 20px',
                          cursor: 'pointer',
                          fontSize: '16px'
                        }}
                      >
                        Create Pixel Art
                      </button>
                    </div>
                  )}
                </div>
                {errors.file && <div className="error" style={{ marginTop: '5px' }}>{errors.file}</div>}
              </div>

              <div className="form-group">
                <label htmlFor="amount">Initial Payment Amount *</label>
                <input
                  type="number"
                  id="amount"
                  name="amount"
                  value={formData.amount}
                  onChange={handleInputChange}
                  className="form-input"
                  step="0.01"
                  placeholder="2.00"
                />
                {errors.amount && <div className="error" style={{ marginTop: '5px' }}>{errors.amount}</div>}
                
                {/* Currency conversion info */}
                {currencyInfo && (
                  <div style={{ 
                    fontSize: '0.9rem', 
                    color: '#888', 
                    marginTop: '8px',
                    padding: '10px',
                    background: 'rgba(255, 215, 0, 0.1)',
                    border: '1px solid #ffd700',
                    borderRadius: '0px'
                  }}>
                    <div style={{ color: '#ffd700' }}>
                      <strong>🏆 Leaderboard amount:</strong> ${parseFloat(formData.amount || 0).toFixed(2)} USD
                    </div>
                    <div style={{ marginBottom: '5px' }}>
                      <strong>💳 You'll pay:</strong> {currencyInfo.symbol}{(parseFloat(formData.amount || 0) * currencyInfo.rate).toFixed(2)} {currencyInfo.currency}
                      
                    </div>
                    {paymentRegion === 'eu' && (
                      <div style={{ fontSize: '0.8rem', color: '#aaa', marginTop: '5px' }}>
                        Rate: {currencyInfo.rate.toFixed(4)} EUR/USD 
                        {currencyInfo.isLive ? <span style={{ color: '#00ff00' }}> (live)</span> : <span style={{ color: '#ffa500' }}> (offline)</span>}
                      </div>
                    )}
                    {paymentRegion === 'us' && (
                      <div style={{ fontSize: '0.8rem', color: '#aaa', marginTop: '5px' }}>
                        Available: Cards
                      </div>
                    )}
                  </div>
                )}

              </div>

              {errors.submit && <div className="error">{errors.submit}</div>}

              <div style={{ textAlign: 'center', marginTop: '30px' }}>
                {/* Terms checkbox - clean and minimal */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '15px' }}>
                  <input
                    type="checkbox"
                    id="terms-checkbox"
                    checked={termsAccepted}
                    onChange={handleTermsChange}
                    style={{
                      transform: 'scale(1.1)',
                      accentColor: '#ffd700'
                    }}
                  />
                  <label
                    htmlFor="terms-checkbox"
                    style={{
                      color: '#cccccc',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    I agree to the{' '}
                    <span
                      onClick={() => setShowTermsModal(true)}
                      style={{
                        color: '#ffd700',
                        textDecoration: 'underline',
                        cursor: 'pointer'
                      }}
                    >
                      Terms and Conditions
                    </span>
                  </label>
                </div>
                
                {errors.terms && <div className="error" style={{ marginBottom: '15px' }}>{errors.terms}</div>}

                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '15px' }}>
                  <button
                    type="submit"
                    disabled={submitting || uploading || !termsAccepted}
                    className="btn btn-primary"
                    style={{ 
                      minWidth: '150px',
                      opacity: !termsAccepted ? 0.6 : 1,
                      cursor: !termsAccepted ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {submitting ? 'Processing...' : `Pay ${formatCurrency(formData.amount || CONFIG.DEFAULT_INITIAL_PAYMENT)} & Create`}
                  </button>
                  
                  <Link to="/" className="btn btn-secondary" style={{ minWidth: '100px' }}>
                    Cancel
                  </Link>
                </div>
              </div>

              {/* Terms and Conditions Modal */}
              {showTermsModal && (
                <div style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  backgroundColor: 'rgba(0, 0, 0, 0.8)',
                  zIndex: 1000,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '20px'
                }}>
                  <div style={{
                    backgroundColor: '#1a1a1a',
                    border: '2px solid #ffd700',
                    borderRadius: '0px',
                    maxWidth: '800px',
                    maxHeight: '80vh',
                    width: '100%',
                    padding: '30px',
                    position: 'relative'
                  }}>
                    <button
                      onClick={() => setShowTermsModal(false)}
                      style={{
                        position: 'absolute',
                        top: '15px',
                        right: '20px',
                        background: 'none',
                        border: 'none',
                        color: '#ffd700',
                        fontSize: '24px',
                        cursor: 'pointer',
                        fontWeight: 'bold'
                      }}
                    >
                      ✕
                    </button>

                    <h3 style={{ color: '#ffd700', marginBottom: '20px', textAlign: 'center' }}>
                      Terms and Conditions for Pay2Win.com
                    </h3>
                    
                    <div style={{ 
                      color: '#cccccc', 
                      lineHeight: '1.5', 
                      fontSize: '13px', 
                      maxHeight: '60vh', 
                      overflowY: 'auto', 
                      padding: '10px',
                      border: '1px solid #444'
                    }}>
                      <p><strong>Effective Date:</strong> {new Date().toLocaleDateString()}</p>
                      <p><strong>By using Pay2Win.com, you agree to these terms. If you disagree, do not use our service.</strong></p>
                      
                      <div style={{ marginTop: '15px' }}>
                        <h4 style={{ color: '#ffd700', fontSize: '14px', marginBottom: '8px' }}>1. Introduction & Service Description</h4>
                        <p>Pay2Win.com ("we," "us," "our") operates a banner display system where users can submit content to appear on our leaderboard. Pay2Win is <strong>essentially a banner advertising system</strong> - nothing more, nothing less. Rankings are determined solely by payment amounts received.</p>
                      </div>

                      <div style={{ marginTop: '15px' }}>
                        <h4 style={{ color: '#ffd700', fontSize: '14px', marginBottom: '8px' }}>2. User Obligations</h4>
                        <p><strong>You agree to:</strong></p>
                        <ul style={{ marginLeft: '15px', marginTop: '5px' }}>
                          <li>Provide accurate information in your submissions</li>
                          <li>Not engage in illegal activities or submit illegal content</li>
                          <li>Not attempt to manipulate rankings through fraudulent means</li>
                          <li>Respect intellectual property rights of others</li>
                        </ul>
                      </div>

                      <div style={{ marginTop: '15px' }}>
                        <h4 style={{ color: '#ffd700', fontSize: '14px', marginBottom: '8px' }}>3. Anonymous Service</h4>
                        <p>Pay2Win operates anonymously - no accounts are created. All submissions are processed without personal registration. You are responsible for remembering your submission details if you wish to reference them later.</p>
                      </div>

                      <div style={{ marginTop: '15px' }}>
                        <h4 style={{ color: '#ffd700', fontSize: '14px', marginBottom: '8px' }}>4. Intellectual Property</h4>
                        <p>The Pay2Win website, logo, and system design are our property. You may share screenshots or links to submissions for promotional purposes. By submitting content, you grant us a license to display it on our platform.</p>
                      </div>

                      <div style={{ marginTop: '15px' }}>
                        <h4 style={{ color: '#ff6b6b', fontSize: '14px', marginBottom: '8px' }}>5. Content Policy & Immediate Removal</h4>
                        <p><strong>Content containing or promoting the following will be removed immediately without refund:</strong></p>
                        <ul style={{ marginLeft: '15px', marginTop: '5px' }}>
                          <li>Hate symbols, hate speech, or discrimination</li>
                          <li>Child pornography, exploitation, or abuse</li>
                          <li>Gore, extreme violence, or graphic content</li>
                          <li>Illegal activities, fraud, or scams</li>
                          <li>Harassment, threats, or doxxing</li>
                          <li>Copyright infringement</li>
                          <li>Spam or malicious content</li>
                        </ul>
                        <p style={{ marginTop: '8px' }}>We determine what constitutes harmful content at our sole discretion. Decisions are final.</p>
                      </div>

                      <div style={{ marginTop: '15px' }}>
                        <h4 style={{ color: '#ffd700', fontSize: '14px', marginBottom: '8px' }}>6. Payment & Refunds</h4>
                        <ul style={{ marginLeft: '15px' }}>
                          <li><strong>All payments are final and non-refundable</strong> except as required by EU law</li>
                          <li>Content removal for policy violations does not entitle you to refunds</li>
                          <li>We do not guarantee specific ranking positions or duration</li>
                          <li>Payment processing is handled by Stripe - subject to their terms</li>
                          <li>Prices are displayed in EUR and include applicable taxes</li>
                        </ul>
                      </div>

                      <div style={{ marginTop: '15px' }}>
                        <h4 style={{ color: '#ffd700', fontSize: '14px', marginBottom: '8px' }}>7. Privacy & Data Collection</h4>
                        <p>We collect minimal data: submission content, payment amounts, and payment information (processed by Stripe). We do not store personal payment details. Your IP address may be logged for security purposes. We do not sell or share your data with third parties except as required for payment processing.</p>
                      </div>

                      <div style={{ marginTop: '15px' }}>
                        <h4 style={{ color: '#ffd700', fontSize: '14px', marginBottom: '8px' }}>8. Disclaimers & Limitation of Liability</h4>
                        <p>Pay2Win is provided "AS IS" and "AS AVAILABLE" without warranties. We are not liable for:</p>
                        <ul style={{ marginLeft: '15px', marginTop: '5px' }}>
                          <li>Service interruptions or downtime</li>
                          <li>Loss of rankings or submissions due to technical issues</li>
                          <li>Third-party content or external links</li>
                          <li>Any indirect, incidental, or consequential damages</li>
                        </ul>
                        <p style={{ marginTop: '8px' }}>Our total liability is limited to the amount you paid us in the 12 months preceding any claim.</p>
                      </div>

                      <div style={{ marginTop: '15px' }}>
                        <h4 style={{ color: '#ffd700', fontSize: '14px', marginBottom: '8px' }}>9. Service Termination</h4>
                        <p>We may terminate or suspend the service at any time with or without notice. We may remove any submission that violates these terms or for any other reason at our discretion.</p>
                      </div>

                      <div style={{ marginTop: '15px' }}>
                        <h4 style={{ color: '#ffd700', fontSize: '14px', marginBottom: '8px' }}>10. Governing Law & Disputes</h4>
                        <p>These terms are governed by Dutch law. Any disputes will be resolved in the courts of Amsterdam, Netherlands. EU consumers retain their mandatory consumer protection rights.</p>
                      </div>

                      <div style={{ marginTop: '15px' }}>
                        <h4 style={{ color: '#ffd700', fontSize: '14px', marginBottom: '8px' }}>11. Changes to Terms</h4>
                        <p>We may update these terms at any time. Changes take effect when posted. Continued use of the service constitutes acceptance of updated terms.</p>
                      </div>

                      <div style={{ marginTop: '15px' }}>
                        <h4 style={{ color: '#ffd700', fontSize: '14px', marginBottom: '8px' }}>12. Immutable System Rules</h4>
                        <p><strong>These terms and the Pay2Win system cannot be changed or modified by users.</strong> The ranking system, content policies, payment structure, and all operational aspects are fixed and determined solely by Pay2Win.com.</p>
                      </div>

                      <div style={{ marginTop: '15px' }}>
                        <h4 style={{ color: '#ffd700', fontSize: '14px', marginBottom: '8px' }}>13. Contact Information</h4>
                        <p>Questions about these terms? Contact us at: <strong>pay2rule@gmail.com</strong></p>
                        <p>Pay2Win.com - Amsterdam, Netherlands</p>
                      </div>

                      <p style={{ marginTop: '15px', fontSize: '12px', color: '#888', textAlign: 'center' }}>
                        <strong>By checking the agreement box and proceeding with payment, you confirm that you have read, understood, and agree to be bound by these terms.</strong>
                      </p>
                    </div>

                    <div style={{ textAlign: 'center', marginTop: '20px' }}>
                      <button
                        onClick={() => setShowTermsModal(false)}
                        className="btn btn-primary"
                        style={{ minWidth: '120px' }}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateSubmission; 